import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  Badge,
  Spinner,
  Divider,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatDate, formatPlate } from "../../utility/funcs";

function Checklist() {
  const { checklistId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data: c } = await supabase
        .from("car_checklists")
        .select(
          "id, notes, created_at, car:cars(id, plate, vin, model:car_models(name, show_as, brand:car_brands(name))), staff:users!staff_id(name)"
        )
        .eq("id", checklistId)
        .single();

      const { data: is } = await supabase
        .from("car_checklist_items")
        .select("*")
        .eq("checklist_id", checklistId)
        .order("id");

      // Resolve signed URLs for photos (bucket is private)
      const withUrls = await Promise.all(
        (is || []).map(async (item) => {
          if (!item.photo_url) return item;
          try {
            const { data: signed } = await supabase.storage
              .from("checklist-photos")
              .createSignedUrl(item.photo_url, 3600);
            return { ...item, signed_photo_url: signed?.signedUrl || null };
          } catch {
            return item;
          }
        })
      );

      setData(c);
      setItems(withUrls);
      setLoading(false);
    };
    fetchData();
  }, [checklistId]);

  if (loading) {
    return (
      <AppWrapper>
        <Page title="Checklist" backAction={{ url: "/checklists" }}>
          <Spinner size="small" />
        </Page>
      </AppWrapper>
    );
  }

  if (!data) {
    return (
      <AppWrapper>
        <Page title="Checklist" backAction={{ url: "/checklists" }}>
          <Text>No encontrado</Text>
        </Page>
      </AppWrapper>
    );
  }

  const fails = items.filter((i) => i.answer === "negative");
  const passes = items.filter((i) => i.answer === "positive");
  const others = items.filter(
    (i) => i.answer !== "negative" && i.answer !== "positive"
  );

  const renderItem = (item) => (
    <div
      key={item.id}
      style={{
        padding: "8px 0",
        borderBottom: "1px solid var(--p-color-border-secondary)",
      }}
    >
      <InlineGrid columns="1fr auto" gap="200">
        <BlockStack gap="050">
          {item.item_question && (
            <Text variant="bodySm" tone="subdued">
              {item.item_question}
            </Text>
          )}
          <Text variant="bodyMd" fontWeight="semibold">
            {item.item_title}
            {item.is_custom && (
              <>
                {" "}
                <Badge tone="attention">Custom</Badge>
              </>
            )}
          </Text>
          {item.signed_photo_url && (
            <a
              href={item.signed_photo_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block", marginTop: 4 }}
            >
              <img
                src={item.signed_photo_url}
                alt={item.item_title}
                style={{
                  maxWidth: 160,
                  maxHeight: 120,
                  borderRadius: 6,
                  objectFit: "cover",
                  border: "1px solid var(--p-color-border)",
                }}
              />
            </a>
          )}
        </BlockStack>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          {item.answer === "positive" ? (
            <Badge tone="success">OK</Badge>
          ) : item.answer === "negative" ? (
            <Badge tone="critical">Falla</Badge>
          ) : (
            <Badge>{item.answer || "—"}</Badge>
          )}
          {item.answer === "negative" && item.dealt_with === true && (
            <Badge tone="info">Atendido</Badge>
          )}
        </div>
      </InlineGrid>
    </div>
  );

  return (
    <AppWrapper>
      <Page
        title={`Checklist #${data.id}`}
        backAction={{ url: "/checklists" }}
      >
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="200">
              <InlineGrid columns="1fr 1fr" gap="200">
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Auto</Text>
                  <Text
                    variant="bodyMd"
                    fontWeight="semibold"
                    onClick={() => data.car?.id && navigate(`/cars/${data.car.id}`)}
                    style={{ cursor: data.car?.id ? "pointer" : "default" }}
                  >
                    {formatPlate(data.car?.plate)} — {data.car?.model?.brand?.name}{" "}
                    {data.car?.model?.show_as || data.car?.model?.name}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Staff</Text>
                  <Text variant="bodyMd">{data.staff?.name || "-"}</Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Fecha</Text>
                  <Text variant="bodyMd">
                    {formatDate(data.created_at?.substring(0, 10))}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Resumen</Text>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Badge tone="success">{`OK: ${passes.length}`}</Badge>
                    <Badge tone="critical">{`Fallas: ${fails.length}`}</Badge>
                    {others.length > 0 && (
                      <Badge>{`Otros: ${others.length}`}</Badge>
                    )}
                  </div>
                </BlockStack>
              </InlineGrid>
              {data.notes && (
                <>
                  <Divider />
                  <BlockStack gap="050">
                    <Text variant="bodySm" tone="subdued">Notas</Text>
                    <Text variant="bodyMd">{data.notes}</Text>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>

          {fails.length > 0 && (
            <Card>
              <BlockStack gap="200">
                <Text variant="headingMd">Fallas ({fails.length})</Text>
                <div>{fails.map(renderItem)}</div>
              </BlockStack>
            </Card>
          )}

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd">Todos los items ({items.length})</Text>
              <div>{items.map(renderItem)}</div>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Checklist;
